import type { IspSportsAlias } from '@ps/web-core-store';
import type { TranslationTag } from '@ps/web-shared-components';

import type { WidgetEffectParams } from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { EventTypeId } from '@swf/sports/types/eventType';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorStatusCode from '@swf/sports/utils/getErrorStatusCode';

import type { BreadcrumbsSuccessResponse } from '../types';

import {
  TXTCLI_Promotions,
  TXTWCL_Future_1,
  TXTWCL_Meetings,
  TXTWCL_My_Bets
} from './effects.messages.json';
import type { WidgetDispatch, WidgetSelect } from './types';

const TRANSLATION_MAP: Partial<Record<IspSportsAlias, TranslationTag>> = {
  sportsPromotion: TXTCLI_Promotions,
  sportsHistoricalBets: TXTWCL_My_Bets,
  sportsSelfExcludedHistoricalBets: TXTWCL_My_Bets,
  sportsAnimalRacingRace: TXTWCL_Meetings,
  sportsAnimalRacingFutureMarket: TXTWCL_Future_1
};

export const effects = {
  async preload(
    _payload: undefined,
    { dispatch, select, widget, rootState, actions }: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { breadcrumbsRequested, clear }: WidgetDispatch = dispatch[widget.uid];
    const widgetSelect: WidgetSelect = select[widget.uid];

    pageChange(actions)?.subscribe(breadcrumbsRequested);

    widget.unmount.subscribe(clear);

    const hasPreloaded = widgetSelect.hasPreloaded(rootState);

    if (!hasPreloaded) {
      await breadcrumbsRequested();
    }
  },
  async breadcrumbsRequested(
    _payload: undefined,
    { dispatch, request, widget, select, rootState }: WidgetEffectParams<BasicSportsWidgetConfig>
  ): Promise<void> {
    const urlParams = select.sports.getUrlParams(rootState);
    const { eventId, competitionId } = urlParams;
    let { eventTypeId } = urlParams;
    const page = select.cms.getPage(rootState);
    const alias = page?.alias;
    const isEventPage = Boolean(eventId);

    if (alias === 'sportsGolfMatches') {
      eventTypeId = String(EventTypeId.GOLF);
    }

    const facetName = isEventPage ? 'COMPETITION' : 'EVENT_TYPE';
    const facetId = isEventPage ? competitionId : eventTypeId;

    const { dataLoadSuccess, dataLoadFailed }: WidgetDispatch = dispatch[widget.uid];

    const translation = alias ? TRANSLATION_MAP[alias] : undefined;
    const sportsRequest = buildSportsRequest(request, rootState);

    try {
      if (translation) {
        dataLoadSuccess(translation);
      } else {
        const {
          body: { data }
        } = await sportsRequest<BreadcrumbsSuccessResponse>({
          path: `/breadcrumbs/${facetName}/${facetId}`
        });
        dataLoadSuccess(data);
      }
    } catch (error) {
      dataLoadFailed(getErrorStatusCode(error));
    }
  }
};
